import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Avatar,
  IconButton,
  Link,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import LinkedIn from '@mui/icons-material/LinkedIn';
import FormatQuote from '@mui/icons-material/FormatQuote';
import { Link as RouterLink } from 'react-router-dom';
import { teamMemberService } from '../services/api';
import { getImageUrl } from '../utils/assets';
import DonateNowButton from '../components/DonateNowButton';
import SimplePageBanner from '../components/SimplePageBanner';

const BRAND_GREEN = '#477C2E';

function MemberPhoto({ member, size = 120, sx = {} }) {
  const src = getImageUrl(member.photo_url);
  return (
    <Avatar
      src={src || undefined}
      alt={member.name}
      sx={{
        width: size,
        height: size,
        mx: 'auto',
        border: '4px solid #fff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        bgcolor: BRAND_GREEN,
        fontFamily: '"Nunito", sans-serif',
        fontWeight: 800,
        fontSize: size * 0.35,
        ...sx,
      }}
    >
      {member.name?.[0]}
    </Avatar>
  );
}

function ContactRow({ member }) {
  if (!member.email && !member.phone && !member.linkedin_url) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1.5 }}>
      {member.email && (
        <IconButton size="small" component="a" href={`mailto:${member.email}`} sx={{ color: BRAND_GREEN, bgcolor: 'rgba(71,124,46,0.08)' }}>
          <EmailOutlined fontSize="small" />
        </IconButton>
      )}
      {member.phone && (
        <IconButton size="small" component="a" href={`tel:${member.phone}`} sx={{ color: BRAND_GREEN, bgcolor: 'rgba(71,124,46,0.08)' }}>
          <PhoneOutlined fontSize="small" />
        </IconButton>
      )}
      {member.linkedin_url && (
        <IconButton size="small" component="a" href={member.linkedin_url} target="_blank" rel="noopener noreferrer" sx={{ color: BRAND_GREEN, bgcolor: 'rgba(71,124,46,0.08)' }}>
          <LinkedIn fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function ContactDetailsPanel({ member }) {
  const rows = [
    member.email
      ? {
          key: 'email',
          label: 'Email',
          value: member.email,
          href: `mailto:${member.email}`,
          icon: <EmailOutlined fontSize="small" />,
        }
      : null,
    member.phone
      ? {
          key: 'phone',
          label: 'Phone',
          value: member.phone,
          href: `tel:${member.phone}`,
          icon: <PhoneOutlined fontSize="small" />,
        }
      : null,
    member.linkedin_url
      ? {
          key: 'linkedin',
          label: 'LinkedIn',
          value: member.linkedin_url,
          href: member.linkedin_url,
          icon: <LinkedIn fontSize="small" />,
          external: true,
        }
      : null,
  ].filter(Boolean);

  if (!rows.length) {
    return (
      <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#9ca3af' }}>
        Contact details are not available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
      {rows.map((row) => (
        <Box
          key={row.key}
          component="a"
          href={row.href}
          target={row.external ? '_blank' : undefined}
          rel={row.external ? 'noopener noreferrer' : undefined}
          sx={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            p: 1.2,
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(249,250,251,0.95))',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(71,124,46,0.35)',
              boxShadow: '0 8px 24px rgba(17,24,39,0.08)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: BRAND_GREEN,
              bgcolor: 'rgba(71,124,46,0.1)',
              flexShrink: 0,
            }}
          >
            {row.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: '#6b7280', textTransform: 'uppercase' }}>
              {row.label}
            </Typography>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 14, fontWeight: 600, color: '#1f2937', wordBreak: 'break-word' }}>
              {row.value}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function TeamMemberCard({ member, onReadMore }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid #eee',
        transition: 'box-shadow 0.25s ease',
        '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.08)' },
      }}
    >
      <Box
        sx={{
          height: 8,
          background: `linear-gradient(90deg, ${BRAND_GREEN}, #8A4B1E)`,
        }}
      />
      <CardContent sx={{ textAlign: 'center', pt: 3, pb: 3 }}>
        <MemberPhoto member={member} size={100} sx={{ mb: 2 }} />
        <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 800, fontSize: 18, color: '#2D2929' }}>
          {member.name}
        </Typography>
        {member.designation && (
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 13, color: '#6b7280', mt: 0.5 }}>
            {member.designation}
          </Typography>
        )}
        {member.bio && (
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 13, color: '#4B4646', mt: 1.5, lineHeight: 1.6 }}>
            {member.bio.length > 100 ? `${member.bio.slice(0, 100)}…` : member.bio}
          </Typography>
        )}
        <ContactRow member={member} />
        <Button
          onClick={() => onReadMore(member)}
          variant="outlined"
          size="small"
          sx={{ mt: 2, borderColor: BRAND_GREEN, color: BRAND_GREEN }}
        >
          Read More
        </Button>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ subtitle, title, description }) {
  return (
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
      <Typography
        sx={{
          fontFamily: '"Caveat", cursive',
          fontWeight: 700,
          fontSize: { xs: 26, md: 32 },
          color: BRAND_GREEN,
          mb: 1,
        }}
      >
        {subtitle}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontFamily: '"Nunito", sans-serif',
          fontWeight: 900,
          fontSize: { xs: 28, md: 40 },
          color: '#2D2929',
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ mt: 1.5, color: '#6b7280', fontFamily: '"Poppins", sans-serif' }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

const TeamPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProfile, setOpenProfile] = useState(null);

  useEffect(() => {
    teamMemberService
      .getAll()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const data = Array.isArray(list) ? list : [];
        setTeamMembers(data.filter((m) => m.role_type === 'team_member'));
      })
      .catch(() => setTeamMembers([]))
      .finally(() => setLoading(false));
  }, []);

  const hasAny = teamMembers.length > 0;

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="The people behind the mission"
        title="Our Team Members"
        description="Our on-ground team members drive every campaign with dedication, care, and impact."
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: BRAND_GREEN }} />
        </Box>
      ) : !hasAny ? (
        <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
          <FormatQuote sx={{ fontSize: 48, color: BRAND_GREEN, opacity: 0.4, mb: 2 }} />
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#6b7280' }}>
            Our team profiles will be published soon. Thank you for your interest.
          </Typography>
        </Container>
      ) : (
        <Box sx={{ bgcolor: '#fff', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <SectionTitle
              subtitle="On the ground every day"
              title="Our Team"
              description="For Founder and Co-Founder profiles, visit the Founders page."
            />
            <Grid container spacing={3}>
              {teamMembers.map((m) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
                  <TeamMemberCard member={m} onReadMore={setOpenProfile} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* CTA strip */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #3d6a28 100%)`,
          py: { xs: 5, md: 7 },
          textAlign: 'center',
          px: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Nunito", sans-serif',
            fontWeight: 800,
            fontSize: { xs: 24, md: 32 },
            color: '#fff',
            mb: 1,
          }}
        >
          Want to work with us?
        </Typography>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: 'rgba(255,255,255,0.9)', mb: 3 }}>
          Volunteer, partner, or support our programs.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <DonateNowButton to="/volunteer" />
          <Box
            component={RouterLink}
            to="/contact"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 3,
              py: 1.25,
              borderRadius: 28,
              border: '2px solid #fff',
              color: '#fff',
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textDecoration: 'none',
              textTransform: 'uppercase',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            Contact Us
          </Box>
        </Box>
      </Box>

      <Dialog
        open={Boolean(openProfile)}
        onClose={() => setOpenProfile(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 24px 70px rgba(15,23,42,0.24)',
          },
        }}
      >
        {openProfile && (
          <>
            <DialogTitle
              sx={{
                py: 3,
                px: { xs: 2.5, md: 3.5 },
                background:
                  'linear-gradient(120deg, rgba(71,124,46,0.14) 0%, rgba(138,75,30,0.10) 100%)',
                borderBottom: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 24, md: 30 }, color: '#1f2937' }}>
                {openProfile.name}
              </Typography>
              {openProfile.designation && (
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: BRAND_GREEN, fontWeight: 600, mt: 0.5 }}>
                  {openProfile.designation}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Grid container>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      minHeight: { xs: 260, md: '100%' },
                      backgroundImage: `url(${getImageUrl(openProfile.photo_url) || ''})`,
                      backgroundColor: '#e5e7eb',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center top',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ p: { xs: 2.5, md: 3.5 }, bgcolor: '#fff' }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#4b5563', lineHeight: 1.85, whiteSpace: 'pre-wrap', fontSize: 15 }}>
                      {openProfile.bio || 'Profile details will be updated soon.'}
                    </Typography>
                    <Divider sx={{ my: 2.5 }} />
                    <ContactDetailsPanel member={openProfile} />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TeamPage;
